import React, { useEffect, useState } from 'react'
import {
  getSubscriptions,
  getSubscriptionsForUser,
  modelLiteratureDocs,
} from '../../../EmailSubscriptionCenter/Data/SubscriptionCenterDataSource.js'
import { isPdfUrl } from '../../../../global/data/url.js'
import SubscribeModalButton from '../../../EmailSubscriptionCenter/Modal/SubscribeModalButton.jsx'
import { isLoggedIntoSubscriptionCenter } from '../../../../global/authentication/authentication-manager.js'
import {
  ACTIVE,
  CAPITAL_GAINS_ESTIMATES_AND_DISTRIBUTIONS,
} from '../../../EmailSubscriptionCenter/Landing/Helper/constants.js'
import { getAllSubscriptionsFromSubscriptionProfile } from '../../../EmailSubscriptionCenter/Landing/Helper/helper.js'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'

function CapitalGainsCalendarYearDistributionReports({
  reports,
  isEtf,
  isMutualFund,
  isIntervalFund,
  subscriptionProfile,
  fundSubscription,
  setFundSubscription,
  setHasFilteredReports,
}) {
  if (!reports || !reports.distributions) {
    return null
  }

  const [subscriptions, setSubscriptions] = useState([])
  const [subscribeDoc, setSubscribeDoc] = useState('')

  useEffect(() => {
    if (allReports.length === 0 && subscriptions.length > 0) {
      setHasFilteredReports(false)
    }
  }, [subscriptions])

  const loadSubscriptionData = async () => {
    const subslist = await getSubscriptions()
    const capGainsSub = subslist?.subscriptions?.filter(
      (sub) => sub.name === CAPITAL_GAINS_ESTIMATES_AND_DISTRIBUTIONS
    )
    if (isLoggedIntoSubscriptionCenter()) {
      const subDocs = getAllSubscriptionsFromSubscriptionProfile(
        (await getSubscriptionsForUser())?.value
      )
      const filterDoc = subDocs?.find(
        (i) =>
          i?.publication?.name === CAPITAL_GAINS_ESTIMATES_AND_DISTRIBUTIONS &&
          i.status === ACTIVE
      )
      setSubscribeDoc(filterDoc?.publication?.publicationID)
    }
    setSubscriptions([{ ...capGainsSub[0], frequency: 'As available' }])
  }

  const showSubscribeOption = () => {
    if (fundSubscription && subscriptions?.length > 0) {
      const hasCapitalGainDoc = fundSubscription?.activeLiterature?.filter(
        (i) => i?.name === CAPITAL_GAINS_ESTIMATES_AND_DISTRIBUTIONS
      )
      if (
        hasCapitalGainDoc?.length > 0 &&
        hasCapitalGainDoc[0]?.selected === !!subscribeDoc
      ) {
        return
      }
      const { status, ...rest } = subscriptions[0] || {}
      const activeLiterature = (
        fundSubscription?.activeLiterature || []
      )?.filter((i) => i?.name !== CAPITAL_GAINS_ESTIMATES_AND_DISTRIBUTIONS)
      const newDocs = {
        ...rest,
        active: status === ACTIVE,
        selected:
          isLoggedIntoSubscriptionCenter() && subscribeDoc ? true : false,
      }
      setFundSubscription({
        ...fundSubscription,
        activeLiterature: [...activeLiterature, newDocs],
      })
    }
  }

  useEffect(() => {
    const docs = modelLiteratureDocs.subscribe((docs) => {
      const filterDoc = docs?.filter(
        (i) =>
          i?.name === CAPITAL_GAINS_ESTIMATES_AND_DISTRIBUTIONS && i?.selected
      )
      setSubscribeDoc(filterDoc?.length > 0 ? filterDoc[0]?.id : '')
    })
    return () => docs.unsubscribe()
  }, [])

  useEffect(() => {
    loadSubscriptionData()
  }, [])

  useEffect(() => {
    showSubscribeOption()
  }, [fundSubscription, subscriptions])

  const renderSubscribeModalButton = (docIndex) => {
    const subDoc = subscriptions[0]
    const currentDoc = {
      ...subDoc,
      id: !isLoggedIntoSubscriptionCenter()
        ? subDoc.id
        : `distributions-${docIndex}-${subDoc.id}`,
      docId: subDoc?.id,
      selected: subscribeDoc ? true : false,
    }
    const subs = [currentDoc]

    if (subs && currentDoc) {
      return (
        <SubscribeModalButton
          handleClicked={({ docId }) => {
            setSubscribeDoc(subscribeDoc ? '' : docId)
          }}
          subscriptionProfile={subscriptionProfile}
          useUnlabeledButton={false}
          useInlineButton={false}
          useCheckBoxButton={true}
          currentDoc={currentDoc}
          subs={subs}
        />
      )
    }
  }

  const productType = isEtf
    ? ' - ETF'
    : isIntervalFund
      ? ' - Interval Funds'
      : ''
  const reportTitleCurrent =
    'Capital Gains and Dividends Current Reports' + productType
  const reportTitleDistribution =
    'Capital Gains and Dividends Distribution Calendar' + productType
  const reportTitleHistorical =
    'Capital Gains and Dividends Historical Reports' + productType
  const reportTitleResources =
    'Capital Gains and Dividends Distribution Resources' + productType

  const getFilteredReports = (reportTitle) => {
    return (
      reports?.distributions?.find(
        (report) => report?.title === reportTitle
      ) || { reports: [] }
    ).reports?.filter((report) =>
      reportTitle === reportTitleCurrent
        ? report?.name?.indexOf('Capital Gain') > -1
        : report?.name
    )
  }

  const currentReports = getFilteredReports(reportTitleCurrent)
  const distributionCalendar = getFilteredReports(reportTitleDistribution)
  const historicalReports = getFilteredReports(reportTitleHistorical)
  const resources = getFilteredReports(reportTitleResources)

  const allReports = [
    ...currentReports,
    ...distributionCalendar,
    ...historicalReports,
    ...resources,
  ]

  const getReportName = (report) => {
    return report && report.url && report.url.toLowerCase().indexOf('.pdf') > 1
      ? report.name + ' (PDF)'
      : report.name
  }

  function renderDocUrlHtml(document) {
    const docHtml = (
      <a
        href={DOMPurify.sanitize(document.url)}
        data-analytics={document.name}
        target={isPdfUrl(document.url) ? '_blank' : '_self'}
        rel="noopener noreferrer"
      >
        {document.name}
      </a>
    )

    return docHtml
  }

  function renderCurrentReports() {
    const title = 'Current Reports'

    const renderSubscribe = isMutualFund || isIntervalFund

    return (
      <table className="Table u-marginBottom4gu u-sm-marginBottom2gu">
        <thead className="Table-head">
          <tr className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
            <th className="Table-header u-bgColorTransparent u-paddingLeft0">
              <h3 className={renderSubscribe ? 'u-marginBottom4gu' : ''}>
                {title}
                {renderSubscribe && (
                  <span className="u-block u-textColorDefault u-textSizeMsr u-paddingTop2gu">
                    All future documents will be emailed to subscribers
                  </span>
                )}
              </h3>
            </th>
            {renderSubscribe && (
              <th className="Table-header u-bgColorTransparent u-paddingRight0">
                <p className="u-textColorLight u-textNormal u-textSecondary u-paddingTop9gu">
                  Email Updates
                </p>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="Table-body">
          {currentReports.map((document, index) => {
            return (
              <tr
                className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0"
                key={`lit${index}`}
              >
                <td className="Table-cell u-paddingLeft0">
                  {renderDocUrlHtml(document)}
                </td>
                {renderSubscribe && subscriptions?.length > 0 && (
                  <td className="Table-cell">
                    {renderSubscribeModalButton(index)}
                  </td>
                )}
              </tr>
            )
            // }
          })}
        </tbody>
      </table>
    )
  }

  return (
    <div
      className="Grid Grid--withGutter hide u-paddingTop4gu"
      id="distribution-reports-content"
    >
      {(currentReports.length !== 0 || historicalReports.length !== 0) && (
        <div className="u-size6of12 u-sm-sizeFull">
          {currentReports.length !== 0 && (
            <div className="u-sm-marginBottom2gu">{renderCurrentReports()}</div>
          )}
          {historicalReports.length !== 0 && (
            <div className="u-sm-marginBottom2gu">
              <table className="Table u-marginBottom4gu u-sm-marginBottom2gu">
                <thead className="Table-head">
                  <tr className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                    <th className="Table-header u-bgColorTransparent u-paddingLeft0">
                      <h3>Historical Reports</h3>
                    </th>
                  </tr>
                </thead>
                <tbody className="Table-body">
                  {historicalReports.map((report, index) => {
                    return (
                      <tr
                        className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0"
                        key={`historical-reports-${index}`}
                      >
                        <td className="Table-cell u-paddingLeft0">
                          <a
                            href={DOMPurify.sanitize(report.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {getReportName(report)}
                          </a>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div
        className={`${
          currentReports.length !== 0 || historicalReports.length !== 0
            ? 'u-size6of12 '
            : ''
        }u-sm-sizeFull`}
      >
        {distributionCalendar.length !== 0 && (
          <div className="u-sm-marginBottom2gu">
            <table className="Table u-marginBottom4gu u-sm-marginBottom2gu">
              <thead className="Table-head">
                <tr className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                  <th className="Table-header u-bgColorTransparent u-paddingLeft0">
                    <h3>Distribution Calendar</h3>
                  </th>
                </tr>
              </thead>
              <tbody className="Table-body">
                {distributionCalendar.map((report, index) => {
                  return (
                    <tr
                      className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0"
                      key={`distribution-calendar-${index}`}
                    >
                      <td className="Table-cell u-paddingLeft0">
                        <a
                          href={DOMPurify.sanitize(report.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {getReportName(report)}
                        </a>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {resources.length !== 0 && (
          <div className="u-sm-marginBottom2gu">
            <table className="Table u-marginBottom4gu u-sm-marginBottom2gu">
              <thead className="Table-head">
                <tr className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                  <th className="Table-header u-bgColorTransparent u-paddingLeft0">
                    <h3>Resources</h3>
                  </th>
                </tr>
              </thead>
              <tbody className="Table-body">
                {resources.map((report, index) => {
                  return (
                    <tr
                      className="Table-row u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0"
                      key={`resources-${index}`}
                    >
                      <td className="Table-cell u-paddingLeft0">
                        <a
                          href={DOMPurify.sanitize(report.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {getReportName(report)}
                        </a>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

CapitalGainsCalendarYearDistributionReports.propTypes = {
  reports: PropTypes.object,
  isEtf: PropTypes.bool.isRequired,
  fundSubscription: PropTypes.object,
  setFundSubscription: PropTypes.func.isRequired,
  subscriptionProfile: PropTypes.object,
}

export default CapitalGainsCalendarYearDistributionReports
